import { useId } from 'react';

/**
 * SVG half-donut gauge for the weekly report's response-performance dashboard.
 * Pure SVG so it prints / exports to PDF cleanly without depending on a
 * charting runtime. Geometry, colour stops, threshold marker, and tick
 * spacing are calibrated to mirror the Plotly `indicator` gauges used in the
 * source Logic-App email template (`Weekly_Security_Report_*.html`).
 */
interface Props {
  value: number | null;
  unit: string;
  label: string;
  /**
   * Three colour-band stops in the same units as `value` and `max`.
   * Region [0, bands[0]] = green, [bands[0], bands[1]] = yellow,
   * [bands[1], bands[2]] = red. Set `bands[2] === max` to fill the dial.
   */
  bands: [number, number, number];
  /** Maximum value on the dial. */
  max: number;
  /** Tick spacing on the axis (every `dtick` units). Default = max / 4. */
  dtick?: number;
  /**
   * Optional threshold marker — drawn as a thin red radial line at this
   * value. Mirrors Plotly's `gauge.threshold`. Default `bands[1]`.
   */
  thresholdValue?: number;
}

export function Gauge({ value, unit, label, bands, max, dtick, thresholdValue }: Props) {
  const uid = useId().replace(/[^a-z0-9]/gi, '');
  const safeVal = value === null || !Number.isFinite(value) ? 0 : Math.max(0, Math.min(max, value));
  const display = value === null || !Number.isFinite(value)
    ? ' - '
    : value < 10 ? value.toFixed(2) : value.toFixed(1);
  const tick = dtick ?? max / 4;
  const threshold = thresholdValue ?? bands[1];

  // Half-donut geometry — viewBox 220x150, centre (110, 120), radius 90.
  const cx = 110, cy = 120, R = 90;
  const polar = (frac: number, r = R) => {
    const angle = Math.PI - Math.PI * frac;
    return { x: cx + r * Math.cos(angle), y: cy - r * Math.sin(angle) };
  };
  const arc = (fStart: number, fEnd: number) => {
    const a = polar(fStart), b = polar(fEnd);
    const large = fEnd - fStart > 0.5 ? 1 : 0;
    return `M ${a.x} ${a.y} A ${R} ${R} 0 ${large} 1 ${b.x} ${b.y}`;
  };

  const f = (v: number) => Math.max(0, Math.min(1, v / max));
  const f1 = f(bands[0]);
  const f2 = f(bands[1]);
  const f3 = f(bands[2]);
  const valueFrac = f(safeVal);

  // Pick number colour & needle colour based on whether we're past the threshold.
  const needleColor = safeVal > threshold ? '#dc3545' : '#28a745';
  const numberColor = value === null
    ? '#6c757d'
    : safeVal > threshold ? '#dc3545' : '#28a745';

  // Build axis tick marks.
  const ticks: number[] = [];
  for (let v = 0; v <= max + 0.0001; v += tick) ticks.push(Math.round(v * 100) / 100);

  // Threshold radial line at the threshold value.
  const tStart = polar(f(threshold), R - 14);
  const tEnd = polar(f(threshold), R + 6);

  // Needle endpoint
  const needle = polar(valueFrac, R - 8);

  return (
    <div className="flex flex-col items-center" id={`gauge-${uid}`}>
      <div style={{ fontSize: 14, fontWeight: 700, color: '#2c3e50', marginBottom: 4 }}>
        {label}
      </div>
      <svg viewBox="0 0 220 160" width="100%" height="200" aria-label={`${label}: ${display} ${unit}`}>
        {/* Bands */}
        <path d={arc(0, f1)} stroke="#d4edda" strokeWidth="22" fill="none" />
        <path d={arc(f1, f2)} stroke="#fff3cd" strokeWidth="22" fill="none" />
        <path d={arc(f2, f3)} stroke="#f8d7da" strokeWidth="22" fill="none" />
        {/* Tick marks */}
        {ticks.map((t, i) => {
          const a = polar(f(t), R - 14);
          const b = polar(f(t), R + 2);
          const lbl = polar(f(t), R + 14);
          return (
            <g key={i}>
              <line x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke="#94a3b8" strokeWidth="1" />
              <text x={lbl.x} y={lbl.y + 3} fontSize="9" textAnchor="middle" fill="#475569">
                {Math.round(t)}
              </text>
            </g>
          );
        })}
        {/* Threshold marker (red radial line) */}
        <line x1={tStart.x} y1={tStart.y} x2={tEnd.x} y2={tEnd.y} stroke="#dc3545" strokeWidth="3" strokeLinecap="round" />
        {/* Needle */}
        <line x1={cx} y1={cy} x2={needle.x} y2={needle.y} stroke={needleColor} strokeWidth="3" strokeLinecap="round" />
        <circle cx={cx} cy={cy} r="6" fill="#2c3e50" />
        {/* Big value text below the dial */}
        <text x={cx} y={150} textAnchor="middle" fontSize="20" fontWeight="700" fill={numberColor}>
          {display}
          <tspan fontSize="13" fontWeight="500" fill="#475569"> {unit}</tspan>
        </text>
      </svg>
    </div>
  );
}
